import type { AuthSplitLayoutProps } from 'src/layouts/auth-split/layout';
import type { ComponentType, ComponentProps, LazyExoticComponent } from 'react';

import { lazy, Suspense } from 'react';

import { SplashScreen } from 'src/components/loading-screen/splash-screen';

// ----------------------------------------------------------------------

// Route configs are built when the app boots, so a layout imported statically there lands in the
// entry chunk together with everything it pulls in (nav, searchbar, drag-and-drop, carousels, icons),
// even for a visitor who only ever sees the sign-in page. These wrappers keep the names and props
// the route files already use but fetch each layout with the first route that renders it.

function withSuspense<C extends ComponentType<any>>(Lazy: LazyExoticComponent<C>) {
  return function LazyLayout(props: ComponentProps<C>) {
    return (
      <Suspense fallback={<SplashScreen />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}

export const DashboardLayout = withSuspense(
  lazy(() => import('src/layouts/dashboard/layout').then((m) => ({ default: m.DashboardLayout })))
);

const loadAuthSplitLayout = () => import('src/layouts/auth-split/layout');

export const AuthSplitLayout = withSuspense(
  lazy(() => loadAuthSplitLayout().then((m) => ({ default: m.AuthSplitLayout })))
);

/**
 * The sign-in page is where most visitors land, so its layout and page are fetched together rather
 * than the page waiting for the layout to arrive and render first.
 */
export function lazyInAuthSplitLayout(
  loadPage: () => Promise<{ default: ComponentType }>,
  layoutProps: Omit<AuthSplitLayoutProps, 'children'> = {}
) {
  return lazy(() =>
    Promise.all([loadAuthSplitLayout(), loadPage()]).then(([{ AuthSplitLayout: Layout }, { default: Page }]) => ({
      default: function PageInAuthSplitLayout() {
        return (
          <Layout {...layoutProps}>
            <Page />
          </Layout>
        );
      },
    }))
  );
}

export const JobNavData = withSuspense(lazy(() => import('src/pages/jobs/container/jobNavData')));

export const LayoutCreateJob = withSuspense(lazy(() => import('src/pages/jobs/organisms/layout-create-job')));
